import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import SaveIcon from '@mui/icons-material/Save';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  TextField,
  Typography
} from '@mui/material';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { useListContext, useResourceContext, useTranslate, useTranslateLabel } from 'ra-core';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RemoveQueryDialog } from './RemoveQueryDialog';
import { SaveQueryDialog } from './SaveQueryDialog';
import { Separator } from './Separator';
import { ACTION_ICON_SX, ACTION_ITEM_SX, ACTION_TEXT_PROPS, GROUP_HEADER_SX } from './styles';
import { extractValidSavedQueries, useSavedQueries } from './useSavedQueries';
import { FilterOption, FilterPickerProps, SavedQuery } from './types';

const DEFAULT_GROUP = 'standard';
const DEFAULT_GROUP_LABELS: Record<string, string> = {
  standard: 'Standard filters'
};

function getSearch(savedQuery: SavedQuery): string {
  const searchParams = new URLSearchParams();
  searchParams.set('filter', JSON.stringify(savedQuery.value.filter));
  if (savedQuery.value.sort?.field) {
    searchParams.set('sort', savedQuery.value.sort.field);
  }
  if (savedQuery.value.sort?.order) {
    searchParams.set('order', savedQuery.value.sort.order);
  }
  searchParams.set('page', '1');
  if (savedQuery.value.perPage) {
    searchParams.set('perPage', String(savedQuery.value.perPage));
  }
  searchParams.set('displayedFilters', JSON.stringify(savedQuery.value.displayedFilters));
  return searchParams.toString();
}

function getFilterGroup(filterElement: ReactElement, defaultGroup: string): string {
  return filterElement.props.filterGroup || filterElement.props.group || defaultGroup;
}

function groupOptions(options: FilterOption[]): Array<[string, FilterOption[]]> {
  const groups = new Map<string, FilterOption[]>();
  options.forEach((option) => {
    const group = option.group || DEFAULT_GROUP;
    groups.set(group, [...(groups.get(group) || []), option]);
  });
  return Array.from(groups.entries());
}

function FilterPicker(props: FilterPickerProps): ReactElement | null {
  const {
    filters: filtersProp,
    groups = DEFAULT_GROUP_LABELS,
    defaultGroup = DEFAULT_GROUP,
    className,
    size = 'small',
    variant = 'text'
  } = props;
  const translate = useTranslate();
  const translateLabel = useTranslateLabel();
  const resource = useResourceContext();
  const navigate = useNavigate();
  const [savedQueries, setSavedQueries] = useSavedQueries(resource);
  const { displayedFilters = {}, filterValues, perPage, setFilters, showFilter, hideFilter, sort } = useListContext();

  const filters = useMemo((): ReactElement[] => filtersProp || [], [filtersProp]);
  const validSavedQueries = useMemo(() => extractValidSavedQueries(savedQueries), [savedQueries]);

  const hiddenFilters = useMemo(
    () =>
      filters.filter(
        (filterElement) =>
          filterElement.props.alwaysOn !== true &&
          !displayedFilters[filterElement.props.source] &&
          typeof get(filterValues, filterElement.props.source) === 'undefined'
      ),
    [filters, displayedFilters, filterValues]
  );

  const allFilterOptions = useMemo(
    () =>
      filters
        .filter((filterElement) => filterElement.props.alwaysOn !== true)
        .map((filterElement) => {
          const label = translateLabel({
            source: filterElement.props.source,
            label: filterElement.props.label,
            resource
          });
          return {
            id: filterElement.props.source,
            label: typeof label === 'string' ? label : filterElement.props.source,
            source: filterElement.props.source,
            group: getFilterGroup(filterElement, defaultGroup),
            defaultValue: filterElement.props.defaultValue
          };
        }),
    [defaultGroup, filters, resource, translateLabel]
  );

  const displayedFilterIds = useMemo(
    () =>
      filters
        .filter(
          (filterElement) =>
            filterElement.props.alwaysOn !== true &&
            (displayedFilters[filterElement.props.source] ||
              typeof get(filterValues, filterElement.props.source) !== 'undefined')
        )
        .map((filterElement) => filterElement.props.source),
    [filters, displayedFilters, filterValues]
  );

  const selectedOptions: FilterOption[] = useMemo(
    () =>
      filters
        .filter(
          (filterElement) =>
            filterElement.props.alwaysOn !== true && displayedFilterIds.includes(filterElement.props.source)
        )
        .map((filterElement) => {
          const label = translateLabel({
            source: filterElement.props.source,
            label: filterElement.props.label,
            resource
          });
          return {
            id: filterElement.props.source,
            label: typeof label === 'string' ? label : filterElement.props.source,
            source: filterElement.props.source,
            group: getFilterGroup(filterElement, defaultGroup),
            defaultValue: filterElement.props.defaultValue
          };
        }),
    [defaultGroup, filters, displayedFilterIds, resource, translateLabel]
  );

  const currentQueryValue = useMemo(
    () => ({ filter: filterValues, sort, perPage, displayedFilters }),
    [filterValues, sort, perPage, displayedFilters]
  );
  const matchedSavedQuery = validSavedQueries.find((savedQuery) => isEqual(savedQuery.value, currentQueryValue));
  const hasFilterValues = !isEqual(filterValues, {});
  const activeCount = selectedOptions.length;
  const hasActiveFilters = activeCount > 0 || hasFilterValues;

  const anchorEl = useRef<HTMLButtonElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [queryName, setQueryName] = useState('');
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(
    () => allFilterOptions.filter((option) => option.label.toLowerCase().includes(search.trim().toLowerCase())),
    [allFilterOptions, search]
  );
  const optionGroups = useMemo(() => groupOptions(filteredOptions), [filteredOptions]);
  const showGroupHeaders = optionGroups.length > 1;

  const handleToggleFilter = useCallback(
    (option: FilterOption): void => {
      if (displayedFilterIds.includes(option.source)) {
        hideFilter(option.source);
        return;
      }
      showFilter(option.source, option.defaultValue === '' ? undefined : option.defaultValue);
    },
    [displayedFilterIds, hideFilter, showFilter]
  );

  const handleRemoveAll = useCallback((): void => {
    setFilters({}, {}, false);
  }, [setFilters]);

  const handleSaveQuery = useCallback((): void => {
    if (!queryName.trim()) return;
    const newQuery: SavedQuery = {
      label: queryName.trim(),
      value: { filter: filterValues, sort, perPage, displayedFilters }
    };
    setSavedQueries([...savedQueries.filter((savedQuery) => savedQuery.label !== queryName.trim()), newQuery]);
    setSaveDialogOpen(false);
    setQueryName('');
  }, [queryName, filterValues, sort, perPage, displayedFilters, savedQueries, setSavedQueries]);

  const handleRemoveQuery = useCallback((): void => {
    if (!matchedSavedQuery) return;
    setSavedQueries(savedQueries.filter((savedQuery) => savedQuery.label !== matchedSavedQuery.label));
    setRemoveDialogOpen(false);
  }, [matchedSavedQuery, savedQueries, setSavedQueries]);

  const handleApplySavedQuery = useCallback(
    (savedQuery: SavedQuery): void => {
      navigate({ search: getSearch(savedQuery) });
      setPopoverOpen(false);
    },
    [navigate]
  );

  const openSaveDialog = useCallback((): void => {
    setPopoverOpen(false);
    setQueryName('');
    setSaveDialogOpen(true);
  }, []);

  const openRemoveDialog = useCallback((): void => {
    setPopoverOpen(false);
    setRemoveDialogOpen(true);
  }, []);

  if (hiddenFilters.length === 0 && validSavedQueries.length === 0 && !hasFilterValues) {
    return null;
  }

  return (
    <>
      <Badge badgeContent={activeCount > 0 ? activeCount : null} color="primary">
        <Button
          className={className}
          ref={anchorEl}
          variant={variant}
          size={size}
          onClick={() => setPopoverOpen(true)}
          startIcon={<FilterListIcon />}
        >
          {translate('ra.action.add_filter')}
        </Button>
      </Badge>
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl.current}
        onClose={() => setPopoverOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { overflow: 'visible', mt: 1, width: 420, boxShadow: 8, border: 1, borderColor: 'grey.200' }
        }}
        elevation={8}
      >
        <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={search}
            variant="outlined"
            placeholder={translate('ra.action.search', { _: 'Search' })}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon fontSize="small" color="action" />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'grey.50',
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused fieldset': { borderColor: 'transparent' }
              }
            }}
          />
          {selectedOptions.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {selectedOptions.map((option) => (
                <Chip key={option.id} label={option.label} size="small" onDelete={() => hideFilter(option.source)} />
              ))}
            </Box>
          ) : null}
        </Box>
        <Box sx={{ maxHeight: 360, overflowY: 'auto', pb: 1 }}>
          {optionGroups.length > 0 ? (
            optionGroups.map(([group, options]) => (
              <Box key={group}>
                {showGroupHeaders ? <Typography sx={GROUP_HEADER_SX}>{groups[group] || group}</Typography> : null}
                {options.map((option) => {
                  const selected = displayedFilterIds.includes(option.source);
                  return (
                    <MenuItem
                      key={option.id}
                      onClick={() => handleToggleFilter(option)}
                      sx={{ minHeight: 44, py: 0.5, px: 1.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox checked={selected} size="small" sx={{ p: 0 }} />
                      </ListItemIcon>
                      <ListItemText primary={option.label} primaryTypographyProps={{ noWrap: true }} />
                    </MenuItem>
                  );
                })}
              </Box>
            ))
          ) : (
            <MenuItem disabled sx={{ minHeight: 44, py: 0.5, px: 1.5 }}>
              <ListItemText primary={translate('ra.navigation.no_results')} />
            </MenuItem>
          )}
        </Box>
        {validSavedQueries.length > 0 || hasActiveFilters ? <Separator /> : null}
        {validSavedQueries.map((savedQuery) =>
          matchedSavedQuery && savedQuery.label === matchedSavedQuery.label ? (
            <MenuItem key={savedQuery.label} onClick={openRemoveDialog} sx={ACTION_ITEM_SX}>
              <ListItemIcon sx={ACTION_ICON_SX}>
                <DeleteIcon />
              </ListItemIcon>
              <ListItemText
                primary={translate('ra.saved_queries.remove_label_with_name', {
                  _: 'Remove query "%{name}"',
                  name: savedQuery.label
                })}
                primaryTypographyProps={ACTION_TEXT_PROPS}
              />
            </MenuItem>
          ) : (
            <MenuItem key={savedQuery.label} onClick={() => handleApplySavedQuery(savedQuery)} sx={ACTION_ITEM_SX}>
              <ListItemIcon sx={ACTION_ICON_SX}>
                <BookmarkBorderIcon />
              </ListItemIcon>
              <ListItemText primary={savedQuery.label} primaryTypographyProps={ACTION_TEXT_PROPS} />
            </MenuItem>
          )
        )}
        {hasActiveFilters && !matchedSavedQuery ? (
          <MenuItem onClick={openSaveDialog} sx={ACTION_ITEM_SX}>
            <ListItemIcon sx={ACTION_ICON_SX}>
              <SaveIcon />
            </ListItemIcon>
            <ListItemText
              primary={translate('ra.saved_queries.new_label', { _: 'Save current query...' })}
              primaryTypographyProps={ACTION_TEXT_PROPS}
            />
          </MenuItem>
        ) : null}
        {hasActiveFilters ? (
          <>
            <Separator />
            <MenuItem onClick={handleRemoveAll} sx={{ ...ACTION_ITEM_SX, color: 'error.main' }}>
              <ListItemIcon sx={ACTION_ICON_SX}>
                <ClearAllIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary={translate('ra.action.remove_all_filters', { _: 'Remove all filters' })}
                primaryTypographyProps={ACTION_TEXT_PROPS}
              />
            </MenuItem>
          </>
        ) : null}
      </Popover>
      <SaveQueryDialog
        open={saveDialogOpen}
        queryName={queryName}
        onClose={() => setSaveDialogOpen(false)}
        onQueryNameChange={setQueryName}
        onSave={handleSaveQuery}
      />
      <RemoveQueryDialog
        open={removeDialogOpen}
        queryLabel={matchedSavedQuery?.label}
        onClose={() => setRemoveDialogOpen(false)}
        onRemove={handleRemoveQuery}
      />
    </>
  );
}

export { FilterPicker };
